/**
 * Tax Service API
 * Client API pour la gestion des Exercices Fiscaux, Périodes Comptables et TVA
 */

import { useAuthStore } from '@/stores/authStore';
import { fetchWithAuth, safeJsonParse } from '@/utils/api';
// Note: companyId vient du JWT (via fetchWithAuth). Pas besoin de header X-Company-Id.
import type {
  FiscalYear,
  FiscalYearSummary,
  CreateFiscalYearInput,
  UpdateFiscalYearInput,
  AccountingPeriod,
  CreateAccountingPeriodInput,
  UpdateAccountingPeriodInput,
  VatPeriod,
  VatPeriodSummary,
  CreateVatPeriodInput,
  UpdateVatPeriodInput,
  AddVatLineInput,
  GenerateVatPeriodsInput,
  FiscalYearStatus,
  AccountingPeriodStatus,
  AccountingPeriodType,
  VatPeriodStatus,
  VatRegime,
} from '../types/tax.types';

/**
 * Erreur API avec code et détails structurés pour traduction i18n
 */
export class ApiError extends Error {
  code: string;
  details?: Record<string, unknown>;
  status: number;

  constructor(message: string, code: string, status: number, details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

/**
 * Vérifie que l'utilisateur a une entreprise sélectionnée.
 * Lance une erreur explicite si ce n'est pas le cas.
 */
const ensureCompanyContext = (): void => {
  const user = useAuthStore.getState().user;
  if (!user?.companyId) {
    throw new Error('Aucune entreprise sélectionnée. Veuillez vous reconnecter ou sélectionner une entreprise.');
  }
};

/**
 * Gère les réponses API et lance des erreurs structurées
 */
const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const errorBody = await safeJsonParse(response).catch(() => ({ message: response.statusText }));

    const errObj = typeof errorBody.error === 'object' ? errorBody.error : null;
    const message = errObj?.message || errorBody.error || errorBody.message || `Erreur ${response.status}`;
    const code = errObj?.code || '';
    const details = errObj?.details || {};

    throw new ApiError(message, code, response.status, details);
  }
  const data = await safeJsonParse(response);
  return data.data || data;
};

// ==================== EXERCICES FISCAUX (FISCAL YEARS) ====================

export const fiscalYearsAPI = {
  async getAll(params?: {
    status?: FiscalYearStatus;
    skip?: number;
    take?: number;
  }): Promise<FiscalYear[]> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.skip) queryParams.append('skip', params.skip.toString());
    if (params?.take) queryParams.append('take', params.take.toString());

    const url = `${API_URL}/tax/fiscal-years${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await fetchWithAuth(url, { method: 'GET' });
    return handleResponse<FiscalYear[]>(response);
  },

  async getCurrent(): Promise<FiscalYear | null> {
    const response = await fetchWithAuth(`${API_URL}/tax/fiscal-years/current`, {
      method: 'GET',
    });
    if (response.status === 404) return null;
    return handleResponse<FiscalYear>(response);
  },

  async getById(id: string): Promise<FiscalYear> {
    const response = await fetchWithAuth(`${API_URL}/tax/fiscal-years/${id}`, {
      method: 'GET',
    });
    return handleResponse<FiscalYear>(response);
  },

  async getSummary(id: string): Promise<FiscalYearSummary> {
    const response = await fetchWithAuth(`${API_URL}/tax/fiscal-years/${id}/summary`, {
      method: 'GET',
    });
    return handleResponse<FiscalYearSummary>(response);
  },

  async create(data: CreateFiscalYearInput): Promise<FiscalYear> {
    const response = await fetchWithAuth(`${API_URL}/tax/fiscal-years`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleResponse<FiscalYear>(response);
  },

  async update(id: string, data: UpdateFiscalYearInput): Promise<FiscalYear> {
    const response = await fetchWithAuth(`${API_URL}/tax/fiscal-years/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return handleResponse<FiscalYear>(response);
  },

  async open(id: string): Promise<FiscalYear> {
    const response = await fetchWithAuth(`${API_URL}/tax/fiscal-years/${id}/open`, {
      method: 'POST',
    });
    return handleResponse<FiscalYear>(response);
  },

  async close(id: string): Promise<FiscalYear> {
    const response = await fetchWithAuth(`${API_URL}/tax/fiscal-years/${id}/close`, {
      method: 'POST',
    });
    return handleResponse<FiscalYear>(response);
  },

  async delete(id: string): Promise<void> {
    const response = await fetchWithAuth(`${API_URL}/tax/fiscal-years/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error((typeof error.error === 'object' ? error.error?.message : error.error) || error.message || 'Erreur lors de la suppression');
    }
  },
};

// ==================== PÉRIODES COMPTABLES (ACCOUNTING PERIODS) ====================

export const accountingPeriodsAPI = {
  async getByFiscalYear(
    fiscalYearId: string,
    params?: {
      status?: AccountingPeriodStatus;
      type?: AccountingPeriodType;
      skip?: number;
      take?: number;
    }
  ): Promise<AccountingPeriod[]> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.type) queryParams.append('type', params.type);
    if (params?.skip) queryParams.append('skip', params.skip.toString());
    if (params?.take) queryParams.append('take', params.take.toString());

    const url = `${API_URL}/tax/fiscal-years/${fiscalYearId}/periods${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await fetchWithAuth(url, { method: 'GET' });
    return handleResponse<AccountingPeriod[]>(response);
  },

  async getCurrent(fiscalYearId: string): Promise<AccountingPeriod | null> {
    const response = await fetchWithAuth(`${API_URL}/tax/fiscal-years/${fiscalYearId}/periods/current`, {
      method: 'GET',
    });
    if (response.status === 404) return null;
    return handleResponse<AccountingPeriod>(response);
  },

  async getById(id: string): Promise<AccountingPeriod> {
    const response = await fetchWithAuth(`${API_URL}/tax/accounting-periods/${id}`, {
      method: 'GET',
    });
    return handleResponse<AccountingPeriod>(response);
  },

  async create(data: CreateAccountingPeriodInput): Promise<AccountingPeriod> {
    const response = await fetchWithAuth(`${API_URL}/tax/accounting-periods`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleResponse<AccountingPeriod>(response);
  },

  async update(id: string, data: UpdateAccountingPeriodInput): Promise<AccountingPeriod> {
    const response = await fetchWithAuth(`${API_URL}/tax/accounting-periods/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return handleResponse<AccountingPeriod>(response);
  },

  async close(id: string): Promise<AccountingPeriod> {
    const response = await fetchWithAuth(`${API_URL}/tax/accounting-periods/${id}/close`, {
      method: 'POST',
    });
    return handleResponse<AccountingPeriod>(response);
  },

  async delete(id: string): Promise<void> {
    const response = await fetchWithAuth(`${API_URL}/tax/accounting-periods/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error((typeof error.error === 'object' ? error.error?.message : error.error) || error.message || 'Erreur lors de la suppression');
    }
  },
};

// ==================== PÉRIODES TVA (VAT PERIODS) ====================

export const vatPeriodsAPI = {
  async getAll(params?: {
    status?: VatPeriodStatus;
    regime?: VatRegime;
    skip?: number;
    take?: number;
  }): Promise<VatPeriod[]> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.regime) queryParams.append('regime', params.regime);
    if (params?.skip) queryParams.append('skip', params.skip.toString());
    if (params?.take) queryParams.append('take', params.take.toString());

    const url = `${API_URL}/tax/vat-periods${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await fetchWithAuth(url, { method: 'GET' });
    return handleResponse<VatPeriod[]>(response);
  },

  async getCurrent(): Promise<VatPeriod | null> {
    const response = await fetchWithAuth(`${API_URL}/tax/vat-periods/current`, {
      method: 'GET',
    });
    if (response.status === 404) return null;
    return handleResponse<VatPeriod>(response);
  },

  async getById(id: string): Promise<VatPeriod> {
    const response = await fetchWithAuth(`${API_URL}/tax/vat-periods/${id}`, {
      method: 'GET',
    });
    return handleResponse<VatPeriod>(response);
  },

  async getSummary(id: string): Promise<VatPeriodSummary> {
    const response = await fetchWithAuth(`${API_URL}/tax/vat-periods/${id}/summary`, {
      method: 'GET',
    });
    return handleResponse<VatPeriodSummary>(response);
  },

  async create(data: CreateVatPeriodInput): Promise<VatPeriod> {
    ensureCompanyContext();
    const response = await fetchWithAuth(`${API_URL}/tax/vat-periods`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleResponse<VatPeriod>(response);
  },

  async update(id: string, data: UpdateVatPeriodInput): Promise<VatPeriod> {
    ensureCompanyContext();
    const response = await fetchWithAuth(`${API_URL}/tax/vat-periods/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return handleResponse<VatPeriod>(response);
  },

  async generateForFiscalYear(data: GenerateVatPeriodsInput): Promise<VatPeriod[]> {
    ensureCompanyContext();
    const response = await fetchWithAuth(`${API_URL}/tax/vat-periods/generate`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleResponse<VatPeriod[]>(response);
  },

  async addLine(id: string, data: AddVatLineInput): Promise<VatPeriod> {
    ensureCompanyContext();
    const response = await fetchWithAuth(`${API_URL}/tax/vat-periods/${id}/lines`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleResponse<VatPeriod>(response);
  },

  async updateLine(id: string, lineId: string, data: Partial<AddVatLineInput>): Promise<VatPeriod> {
    ensureCompanyContext();
    const response = await fetchWithAuth(`${API_URL}/tax/vat-periods/${id}/lines/${lineId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return handleResponse<VatPeriod>(response);
  },

  async deleteLine(id: string, lineId: string): Promise<VatPeriod> {
    ensureCompanyContext();
    const response = await fetchWithAuth(`${API_URL}/tax/vat-periods/${id}/lines/${lineId}`, {
      method: 'DELETE',
    });
    return handleResponse<VatPeriod>(response);
  },

  async markReady(id: string): Promise<VatPeriod> {
    ensureCompanyContext();
    const response = await fetchWithAuth(`${API_URL}/tax/vat-periods/${id}/ready`, {
      method: 'POST',
    });
    return handleResponse<VatPeriod>(response);
  },

  async revertToDraft(id: string): Promise<VatPeriod> {
    ensureCompanyContext();
    const response = await fetchWithAuth(`${API_URL}/tax/vat-periods/${id}/draft`, {
      method: 'POST',
    });
    return handleResponse<VatPeriod>(response);
  },

  async markDeclared(id: string, externalReference?: string): Promise<VatPeriod> {
    ensureCompanyContext();
    const response = await fetchWithAuth(`${API_URL}/tax/vat-periods/${id}/declare`, {
      method: 'POST',
      body: JSON.stringify({ externalReference }),
    });
    return handleResponse<VatPeriod>(response);
  },

  async markPaid(id: string): Promise<VatPeriod> {
    ensureCompanyContext();
    const response = await fetchWithAuth(`${API_URL}/tax/vat-periods/${id}/pay`, {
      method: 'POST',
    });
    return handleResponse<VatPeriod>(response);
  },

  async calculateFromInvoices(id: string): Promise<VatPeriod> {
    ensureCompanyContext();
    const response = await fetchWithAuth(`${API_URL}/tax/vat-periods/${id}/calculate`, {
      method: 'POST',
    });
    return handleResponse<VatPeriod>(response);
  },

  async delete(id: string): Promise<void> {
    ensureCompanyContext();
    const response = await fetchWithAuth(`${API_URL}/tax/vat-periods/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error((typeof error.error === 'object' ? error.error?.message : error.error) || error.message || 'Erreur lors de la suppression');
    }
  },

  async getCA3(id: string): Promise<CA3FormData> {
    const response = await fetchWithAuth(`${API_URL}/tax/vat-periods/${id}/ca3`, {
      method: 'GET',
    });
    return handleResponse<CA3FormData>(response);
  },

  async getPdf(id: string): Promise<Blob> {
    const response = await fetchWithAuth(`${API_URL}/tax/vat-periods/${id}/pdf`, {
      method: 'GET',
    });
    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error((typeof error.error === 'object' ? error.error?.message : error.error) || error.message || 'Erreur lors de la génération PDF');
    }
    return response.blob();
  },

  // Alias for generateForFiscalYear
  async generate(data: GenerateVatPeriodsInput): Promise<VatPeriod[]> {
    return this.generateForFiscalYear(data);
  },
};

// ==================== PRÉVISIONS TVA (ML-POWERED) ====================

export interface VATForecastMonth {
  month: number;
  year: number;
  label: string;
  shortLabel: string;
  predictedCollected: number;
  predictedDeductible: number;
  predictedToPay: number;
  confidence: number;
  trend: 'up' | 'down' | 'stable';
  trendPercent: number;
  seasonalFactor: number;
}

export interface VATForecastAlert {
  type: 'info' | 'warning' | 'error';
  message: string;
  month: string;
}

export interface VATForecastResponse {
  forecasts: VATForecastMonth[];
  alerts: VATForecastAlert[];
  basedOn: string[];
  provider: 'openai' | 'heuristics';
  mlEnhanced: boolean;
}

export interface VATForecastStatus {
  mlWorkerAvailable: boolean;
  fallbackAvailable: boolean;
}

export const vatForecastAPI = {
  /**
   * Générer des prévisions TVA via ML Worker
   */
  async generate(params?: {
    monthsAhead?: number;
    currentCollected?: number;
    currentDeductible?: number;
    monthlyRevenue?: number;
    monthlyExpenses?: number;
  }): Promise<VATForecastResponse> {
    const response = await fetchWithAuth(`${API_URL}/tax/vat-periods/forecast`, {
      method: 'POST',
      body: JSON.stringify(params || {}),
    });
    return handleResponse<VATForecastResponse>(response);
  },

  /**
   * Vérifier le statut du service ML Worker
   */
  async getStatus(): Promise<VATForecastStatus> {
    try {
      const response = await fetchWithAuth(`${API_URL}/tax/vat-periods/forecast/status`, {
        });
      return handleResponse<VATForecastStatus>(response);
    } catch {
      return { mlWorkerAvailable: false, fallbackAvailable: true };
    }
  },
};

// ==================== TYPES CA3 ====================

/** Informations entreprise pour la déclaration CA3 */
export interface CA3CompanyInfo {
  siren: string;
  vatNumber: string;
  companyName: string;
  address: string;
  postalCode: string;
  city: string;
  apeCode?: string;
  taxOffice?: string;
}

/** Période de déclaration CA3 */
export interface CA3Period {
  year: number;
  startMonth: number;
  endMonth: number;
  dueDate: string;
  periodType: 'MONTHLY' | 'QUARTERLY';
}

/** Section A - Opérations imposables (TVA collectée) */
export interface CA3SectionA {
  ligne01_base20: number;
  ligne02_base10: number;
  ligne03_base5_5: number;
  ligne04_base2_1: number;
  ligne05_acquisitionsIntracom: number;
  ligne06_achatsEtrangers: number;
  ligne07_importations: number;
  ligne08_tva20: number;
  ligne09_tva10: number;
  ligne0A_tva5_5: number;
  ligne0B_tva2_1: number;
  ligne0C_totalTvaBrute: number;
  ligne0D_tvaIntracom: number;
  ligne0E_regularisation: number;
  ligne13_totalTvaBruteDue: number;
}

/** Section B - TVA déductible */
export interface CA3SectionB {
  ligne19_tvaImmobilisations: number;
  ligne20_tvaAutresBiens: number;
  ligne21_autreTva: number;
  ligne22_creditReporte: number;
  ligne23_totalTvaDeductible: number;
}

/** Section C - TVA nette à payer ou crédit */
export interface CA3SectionC {
  ligne25_tvaNetteAPayer: number;
  ligne26_creditTva: number;
  ligne27_creditAReporter: number;
  ligne28_demandeRemboursement: number;
  ligne29_taxesAssimilees: number;
  ligne30_totalAPayer: number;
  ligne31_tvaMonaco: number;
  ligne32_sommeFinale: number;
}

/** Section D - Mentions diverses */
export interface CA3SectionD {
  ligneAA_exportHorsUe: number;
  ligneAB_livraisonsIntracom: number;
  ligneAC_autresNonImposables: number;
  ligneAD_totalNonImposables: number;
}

/** Détail TVA collectée par taux */
export interface CA3TvaCollecteeDetail {
  taux: number;
  baseHT: number;
  montantTva: number;
  nombreFactures: number;
}

/** Détail TVA déductible par catégorie */
export interface CA3TvaDeductibleDetail {
  categorie: 'IMMOBILISATIONS' | 'AUTRES_BIENS_SERVICES';
  taux: number;
  baseHT: number;
  montantTva: number;
  nombreDepenses: number;
}

/** Formulaire CA3 complet */
export interface CA3FormData {
  companyInfo: CA3CompanyInfo;
  period: CA3Period;
  sectionA: CA3SectionA;
  sectionB: CA3SectionB;
  sectionC: CA3SectionC;
  sectionD?: CA3SectionD;
  detailCollectee: CA3TvaCollecteeDetail[];
  detailDeductible: CA3TvaDeductibleDetail[];
  metadata: {
    vatPeriodId: string;
    generatedAt: string;
    version: string;
    status: 'DRAFT' | 'READY' | 'SUBMITTED';
  };
}

// ==================== DÉCLARATIONS TVA (VAT DECLARATIONS) ====================

export interface VatDeclaration {
  id: string;
  vatPeriodId: string;
  companyId: string;
  tenantId: string;
  formType: 'CA3' | 'CA3_SIMPLIFIED' | 'CA12';
  declarationType: 'INITIAL' | 'CORRECTIVE' | 'REPLACEMENT';
  status: 'DRAFT' | 'READY' | 'TRANSMITTED' | 'ACCEPTED' | 'REJECTED' | 'CANCELLED';
  transmissionMode: 'MANUAL_EXPORT' | 'PARTNER' | 'DIRECT';
  ediFileName?: string | null;
  ediFileSize?: number | null;
  ediGeneratedAt?: string | null;
  transmittedAt?: string | null;
  transmissionId?: string | null;
  dgfipStatus?: string | null;
  dgfipMessage?: string | null;
  dgfipAcknowledgementId?: string | null;
  partnerId?: string | null;
  notes?: string | null;
  cancelledAt?: string | null;
  cancelledBy?: string | null;
  cancelReason?: string | null;
  createdAt: string;
  updatedAt: string;
  VatPeriod?: {
    id: string;
    name: string;
    startDate: string;
    endDate: string;
    totalCollected: number;
    totalDeductible: number;
    amountToPay: number;
  };
}

export interface EdiPartner {
  id: string;
  name: string;
  description: string;
  supported: boolean;
}

export interface CreateDeclarationInput {
  vatPeriodId: string;
  formType?: 'CA3' | 'CA3_SIMPLIFIED' | 'CA12';
  declarationType?: 'INITIAL' | 'CORRECTIVE' | 'REPLACEMENT';
  transmissionMode?: 'MANUAL_EXPORT' | 'PARTNER' | 'DIRECT';
  notes?: string;
}

export const vatDeclarationsAPI = {
  async getAll(params?: {
    vatPeriodId?: string;
    status?: string;
    skip?: number;
    take?: number;
  }): Promise<VatDeclaration[]> {
    const queryParams = new URLSearchParams();
    if (params?.vatPeriodId) queryParams.append('vatPeriodId', params.vatPeriodId);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.skip) queryParams.append('skip', params.skip.toString());
    if (params?.take) queryParams.append('take', params.take.toString());

    const url = `${API_URL}/tax/declarations${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await fetchWithAuth(url, { method: 'GET' });
    return handleResponse<VatDeclaration[]>(response);
  },

  async getById(id: string): Promise<VatDeclaration> {
    const response = await fetchWithAuth(`${API_URL}/tax/declarations/${id}`, {
      method: 'GET',
    });
    return handleResponse<VatDeclaration>(response);
  },

  async getPartners(): Promise<EdiPartner[]> {
    const response = await fetchWithAuth(`${API_URL}/tax/declarations/partners`, {
      method: 'GET',
    });
    return handleResponse<EdiPartner[]>(response);
  },

  async create(data: CreateDeclarationInput): Promise<VatDeclaration> {
    ensureCompanyContext();
    const response = await fetchWithAuth(`${API_URL}/tax/declarations`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleResponse<VatDeclaration>(response);
  },

  async markReady(id: string): Promise<VatDeclaration> {
    ensureCompanyContext();
    const response = await fetchWithAuth(`${API_URL}/tax/declarations/${id}/ready`, {
      method: 'POST',
    });
    return handleResponse<VatDeclaration>(response);
  },

  async transmit(id: string, partnerId?: string, transmissionMode?: string): Promise<{
    success: boolean;
    transmissionId?: string;
    timestamp?: string;
    acknowledgement?: string;
    errors?: string[];
  }> {
    ensureCompanyContext();
    const response = await fetchWithAuth(`${API_URL}/tax/declarations/${id}/transmit`, {
      method: 'POST',
      body: JSON.stringify({ partnerId, transmissionMode }),
    });
    return handleResponse(response);
  },

  async downloadEdi(id: string): Promise<Blob> {
    ensureCompanyContext();
    const response = await fetchWithAuth(`${API_URL}/tax/declarations/${id}/edi`, {
      method: 'GET',
    });
    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error((typeof error.error === 'object' ? error.error?.message : error.error) || error.message || 'Erreur de téléchargement');
    }
    return response.blob();
  },

  async cancel(id: string, reason: string): Promise<VatDeclaration> {
    ensureCompanyContext();
    const response = await fetchWithAuth(`${API_URL}/tax/declarations/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ reason }),
    });
    return handleResponse<VatDeclaration>(response);
  },

  async previewEdi(vatPeriodId: string): Promise<{
    fileName: string;
    size: number;
    preview: string;
    fullContent: string;
  }> {
    ensureCompanyContext();
    const response = await fetchWithAuth(`${API_URL}/tax/vat-periods/${vatPeriodId}/edi/preview`, {
      method: 'GET',
    });
    return handleResponse(response);
  },

  async delete(id: string): Promise<void> {
    ensureCompanyContext();
    const response = await fetchWithAuth(`${API_URL}/tax/declarations/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error((typeof error.error === 'object' ? error.error?.message : error.error) || error.message || 'Erreur lors de la suppression');
    }
  },

  async getEdiPartners(): Promise<EdiPartner[]> {
    const response = await fetchWithAuth(`${API_URL}/tax/declarations/partners`, {
      method: 'GET',
    });
    return handleResponse<EdiPartner[]>(response);
  },
};

// Export service unifié
export const taxService = {
  fiscalYears: fiscalYearsAPI,
  accountingPeriods: accountingPeriodsAPI,
  vatPeriods: vatPeriodsAPI,
  vatForecast: vatForecastAPI,
  vatDeclarations: vatDeclarationsAPI,
};
